import React, { useCallback, useMemo, useState } from "react";

import {
  Container,
  Step,
  StepButton,
  StepProps,
  Typography,
  stepButtonClasses,
  stepIconClasses,
  stepLabelClasses,
  styled,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import ShortTextIcon from "@mui/icons-material/ShortText";

import { FireworksIcon, PillIcon, TargetIcon } from "@robin/common";

import TimelineChart from "./TimelineChart";
import TimelineItemDetails from "./TimelineItemDetails";
import { Datapoint, SeriesType } from "../hooks/useVisualisationData";
import { HorizontalStack } from "./primitives/Stacks";
import { PatientData } from "../models/patient-data";
import { DateTimeUtils } from "../utils/dateTimeUtils";
import { useTranslation } from "react-i18next";

const StyledStep = styled(Step, {
  shouldForwardProp: (propName: string) => !["hasDetails"].includes(propName),
})<StepProps & { hasDetails?: boolean }>(({ hasDetails, theme }) => ({
  padding: theme.spacing(0),
  [`& .${stepButtonClasses.root}`]: {
    borderRadius: theme.shape.borderRadius * 3,
    display: "block",
    width: "100%",
    ...(hasDetails && {
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
    }),
    ...(!hasDetails && {
      "&:hover": {
        cursor: "default",
      },
    }),
  },
  [`& .${stepLabelClasses.root}`]: {
    display: "flex",
    flexDirection: "row",
    padding: theme.spacing(0),
  },
  [`& .${stepIconClasses.root}`]: {
    height: theme.spacing(1),
    margin: theme.spacing(0, 1),
    width: theme.spacing(1),
  },
  [`& .${stepIconClasses.text}`]: {
    fill: "transparent",
  },
  [theme.breakpoints.down("md")]: {
    [`& .${stepLabelClasses.iconContainer}`]: {
      display: "none",
    },
    [`& .${stepLabelClasses.root}`]: {
      paddingLeft: 0,
    },
  },
}));

// Wrapping StyledStep in a functional component
const StyledStepComponent: React.FC<StepProps & { hasDetails: boolean }> = ({
  hasDetails,
  ...props
}) => {
  return <StyledStep hasDetails={hasDetails} {...props} />;
};

// Using React.memo to memoize StyledStepComponent
const MemoizedStyledStep = React.memo(StyledStepComponent);

const StyledTime = styled(Typography)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
  lineHeight: 1,
  "span:last-child": {
    color: theme.palette.text.secondary,
  },
  [theme.breakpoints.down("lg")]: {
    alignItems: "center",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
  [theme.breakpoints.up("md")]: {
    justifyContent: "space-around",
    span: {
      flex: "1 1 50%",
      textAlign: "center",
    },
  },
}));

export const TimelineItem: React.FC<
  StepProps & {
    datapoint: Datapoint;
    references?: Pick<PatientData["data"], "medicaments" | "weeklyTargets">;
    series: SeriesType[];
  }
> = ({ datapoint, references, series, ...stepProps }) => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const [active, setActive] = useState<boolean>(false);

  const date = useMemo(() => {
    return new Date(datapoint.timestamp).toLocaleDateString(currentLanguage, {
      day: "numeric",
      month: "numeric",
    });
  }, [datapoint.timestamp, currentLanguage]);

  const time = useMemo(
    () => DateTimeUtils.formatTime(datapoint.timestamp, currentLanguage),
    [datapoint.timestamp, currentLanguage],
  );

  const hasDetails = useMemo(() => {
    const { experiences, medicamentLogs, notes, weeklyTargetLogs } =
      datapoint.extras;
    return (
      experiences.length > 0 ||
      medicamentLogs.length > 0 ||
      notes.length > 0 ||
      weeklyTargetLogs.length > 0
    );
  }, [datapoint]);

  const onClick = useCallback(() => {
    if (!hasDetails) return;
    setActive((s) => !s);
  }, [hasDetails]);

  return (
    <MemoizedStyledStep
      active={active}
      completed={false}
      expanded={active}
      hasDetails={hasDetails}
      {...stepProps}
    >
      <StepButton color="inherit" onClick={onClick}>
        <Grid
          container
          alignContent="center"
          alignItems="center"
          spacing={{ xs: 1, md: 2 }}
        >
          <Grid xs={2}>
            <StyledTime color="text.primary" display="block" variant="caption">
              <span>{date}</span>
              <span>{time}</span>
            </StyledTime>
          </Grid>
          <Grid xs={8}>
            <Container disableGutters maxWidth="sm">
              <TimelineChart data={datapoint.values} series={series} />
            </Container>
          </Grid>
          <Grid xs={2}>
            <HorizontalStack alignItems="center" justifyContent="center">
              <HorizontalStack
                alignItems="center"
                spacing={1}
                sx={{ display: { xs: "none", md: "flex" }, flexGrow: 1 }}
              >
                {datapoint.extras.experiences.length > 0 && (
                  <FireworksIcon
                    fontSize="small"
                    sx={{ color: "text.disabled" }}
                  />
                )}
                {datapoint.extras.notes.length > 0 && (
                  <ShortTextIcon
                    fontSize="small"
                    sx={{ color: "text.disabled" }}
                  />
                )}
                {datapoint.extras.medicamentLogs.length > 0 && (
                  <PillIcon fontSize="small" sx={{ color: "text.disabled" }} />
                )}
                {datapoint.extras.weeklyTargetLogs.length > 0 && (
                  <TargetIcon
                    fontSize="small"
                    sx={{ color: "text.disabled" }}
                  />
                )}
              </HorizontalStack>
              {hasDetails && (
                <ExpandMoreIcon
                  sx={({ transitions, palette }) => ({
                    color: palette.text.secondary,
                    transition: transitions.create("transform"),
                    ...(active ? { transform: "rotate(-180deg)" } : {}),
                  })}
                />
              )}
            </HorizontalStack>
          </Grid>
        </Grid>
      </StepButton>
      {hasDetails && (
        <TimelineItemDetails data={datapoint} references={references} />
      )}
    </MemoizedStyledStep>
  );
};

export default TimelineItem;
